import { forwardRef, useImperativeHandle, useMemo, useRef } from 'react';
import { viridisColor, viridisTextColor, normalizeValue } from '@/lib/viridis';
import {
  computeHeatmapColorScale,
  formatExpirationLabel,
  selectHeatmapDisplayRows,
} from '@/lib/heatmapTable.utils';
import type { ExposureRow, ExposureType, Ticker } from '@/store/heatmapStore';
import type { PriceDistributionBucket } from '@/types/session';

export interface HeatmapScrollOptions {
  /** True when centering on load or a view change rather than a user click. */
  automatic?: boolean;
}

export interface HeatmapTableHandle {
  scrollToSpot: (options?: HeatmapScrollOptions) => void;
  scrollToKing: (options?: HeatmapScrollOptions) => void;
}

interface HeatmapTableProps {
  rows: ExposureRow[];
  expirations: string[];
  spotPrice: number;
  visibleStrikeCount?: number | null;
  type?: ExposureType;
  ticker?: Ticker;
  timeSpent?: PriceDistributionBucket[] | null;
}

const STRIKE_COLUMN_WIDTH = 58;
const DATA_COLUMN_WIDTH = 82;
const TIME_SPENT_COLUMN_WIDTH = 116;
const ROW_HEIGHT = 20;
const CELL_HORIZONTAL_PADDING = 6;

export const HeatmapTable = forwardRef<HeatmapTableHandle, HeatmapTableProps>(function HeatmapTable(
  { rows, expirations, spotPrice, visibleStrikeCount, type = 'gex', ticker, timeSpent },
  ref,
) {
  const spotRowRef = useRef<HTMLTableRowElement | null>(null);
  const kingCellRef = useRef<HTMLTableCellElement | null>(null);
  const displayRows = useMemo(
    () => selectHeatmapDisplayRows(rows, expirations, spotPrice, ticker, visibleStrikeCount),
    [rows, expirations, spotPrice, ticker, visibleStrikeCount],
  );
  const timeSpentMap = useMemo(() => {
    return new Map((timeSpent ?? []).map((bucket) => [bucket.strike, bucket]));
  }, [timeSpent]);
  const showTimeSpent = timeSpent != null && timeSpent.length > 0;
  const maxTimeSpentPercent = useMemo(() => {
    return Math.max(1, ...(timeSpent ?? []).map((bucket) => bucket.percent));
  }, [timeSpent]);

  const { colorMin, colorMax, spotRowIndex, kingCell } = useMemo(
    () => computeHeatmapColorScale(displayRows, expirations, spotPrice, type, ticker),
    [displayRows, expirations, spotPrice, type, ticker],
  );

  useImperativeHandle(ref, () => ({
    scrollToSpot: () => {
      spotRowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    },
    scrollToKing: () => {
      kingCellRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'center',
      });
    },
  }));

  if (displayRows.length === 0) {
    return (
      <div className="flex h-64 items-center justify-center font-mono text-[11px] text-zinc-500">
        No data available
      </div>
    );
  }

  const minTableWidth =
    STRIKE_COLUMN_WIDTH +
    expirations.length * DATA_COLUMN_WIDTH +
    (showTimeSpent ? TIME_SPENT_COLUMN_WIDTH : 0);
  const dataColumnWidth =
    expirations.length > 0
      ? `calc((100% - ${STRIKE_COLUMN_WIDTH}px - ${
          showTimeSpent ? TIME_SPENT_COLUMN_WIDTH : 0
        }px) / ${expirations.length})`
      : `${DATA_COLUMN_WIDTH}px`;

  return (
    <div className="flex min-h-0 flex-1 flex-col px-3 min-[1024px]:px-6">
      <div
        className="min-h-0 flex-1 overflow-auto [overscroll-behavior:contain]"
        style={{ WebkitOverflowScrolling: 'touch', touchAction: 'pan-x pan-y' }}
      >
        <table
          aria-label={`${type.toUpperCase()} heatmap`}
          className="min-w-full border-collapse font-mono"
          style={{
            tableLayout: 'fixed',
            width: '100%',
            minWidth: `${minTableWidth}px`,
            touchAction: 'pan-x pan-y',
            WebkitUserSelect: 'none',
            userSelect: 'none',
          }}
        >
          <colgroup>
            <col
              style={{
                width: STRIKE_COLUMN_WIDTH,
                minWidth: STRIKE_COLUMN_WIDTH,
                maxWidth: STRIKE_COLUMN_WIDTH,
              }}
            />
            {expirations.map((exp) => (
              <col key={exp} style={{ width: dataColumnWidth, minWidth: DATA_COLUMN_WIDTH }} />
            ))}
            {showTimeSpent && (
              <col
                style={{
                  width: TIME_SPENT_COLUMN_WIDTH,
                  minWidth: TIME_SPENT_COLUMN_WIDTH,
                  maxWidth: TIME_SPENT_COLUMN_WIDTH,
                }}
              />
            )}
          </colgroup>
          <thead>
            <tr>
              <th
                className="sticky left-0 top-0 z-40 bg-background px-1 py-1 text-left text-[9px] font-semibold text-white/85"
                style={{
                  width: STRIKE_COLUMN_WIDTH,
                  minWidth: STRIKE_COLUMN_WIDTH,
                  maxWidth: STRIKE_COLUMN_WIDTH,
                  borderBottom: '1px solid rgba(255,255,255,0.1)',
                  boxShadow: '8px 0 16px rgba(10,10,10,0.3)',
                  touchAction: 'pan-x pan-y',
                  pointerEvents: 'none',
                }}
              >
                Strike
              </th>
              {expirations.map((exp) => (
                <th
                  key={exp}
                  className="sticky top-0 z-30 bg-background px-1 py-1 text-center text-[9px] font-semibold text-white/85"
                  style={{
                    width: dataColumnWidth,
                    minWidth: DATA_COLUMN_WIDTH,
                    borderBottom: '1px solid rgba(255,255,255,0.1)',
                    touchAction: 'pan-x pan-y',
                    pointerEvents: 'none',
                  }}
                >
                  {formatExpirationLabel(exp)}
                </th>
              ))}
              {showTimeSpent && (
                <th
                  className="sticky top-0 z-30 bg-background px-2 py-1 text-left text-[9px] font-semibold uppercase text-white/85"
                  style={{
                    width: TIME_SPENT_COLUMN_WIDTH,
                    minWidth: TIME_SPENT_COLUMN_WIDTH,
                    borderBottom: '1px solid rgba(255,255,255,0.1)',
                    touchAction: 'pan-x pan-y',
                    pointerEvents: 'none',
                  }}
                >
                  Time Spent %
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {displayRows.map((row, rowIdx) => {
              const isSpotRow = rowIdx === spotRowIndex;

              return (
                <tr key={row.strike} ref={isSpotRow ? spotRowRef : undefined}>
                  <td
                    className="sticky left-0 z-10"
                    style={{
                      width: STRIKE_COLUMN_WIDTH,
                      minWidth: STRIKE_COLUMN_WIDTH,
                      maxWidth: STRIKE_COLUMN_WIDTH,
                      height: ROW_HEIGHT,
                      padding: `1px ${CELL_HORIZONTAL_PADDING}px`,
                      fontSize: 11,
                      fontWeight: isSpotRow ? 700 : 500,
                      color: isSpotRow ? '#000' : 'rgba(255,255,255,0.8)',
                      backgroundColor: isSpotRow ? '#fff' : 'var(--background)',
                      textAlign: 'left',
                      whiteSpace: 'nowrap',
                      boxShadow: isSpotRow ? undefined : '8px 0 16px rgba(10,10,10,0.28)',
                      touchAction: 'pan-x pan-y',
                      WebkitUserSelect: 'none',
                      userSelect: 'none',
                      pointerEvents: 'none',
                      ...(isSpotRow
                        ? {
                            borderRadius: '4px 0 0 4px',
                            clipPath:
                              'polygon(0 0, calc(100% - 8px) 0, 100% 50%, calc(100% - 8px) 100%, 0 100%)',
                          }
                        : {}),
                    }}
                  >
                    {row.strike.toFixed(1)}
                  </td>

                  {expirations.map((exp) => {
                    const cell = row.cells[exp];
                    if (cell == null) {
                      return (
                        <td
                          key={exp}
                          className="bg-background"
                          style={{
                            width: dataColumnWidth,
                            minWidth: DATA_COLUMN_WIDTH,
                            height: ROW_HEIGHT,
                            padding: `1px ${CELL_HORIZONTAL_PADDING}px`,
                            touchAction: 'pan-x pan-y',
                          }}
                        />
                      );
                    }

                    const t = normalizeValue(cell.raw, colorMin, colorMax);
                    const bgColor = viridisColor(t);
                    const textColor = viridisTextColor(t);
                    const isKingCell = kingCell?.rowIndex === rowIdx && kingCell.expiration === exp;

                    return (
                      <td
                        key={exp}
                        ref={isKingCell ? kingCellRef : undefined}
                        style={{
                          width: dataColumnWidth,
                          minWidth: DATA_COLUMN_WIDTH,
                          height: ROW_HEIGHT,
                          padding: `1px ${CELL_HORIZONTAL_PADDING}px`,
                          fontSize: 10,
                          fontWeight: isKingCell ? 700 : 400,
                          color: textColor,
                          backgroundColor: bgColor,
                          textAlign: 'right',
                          whiteSpace: 'nowrap',
                          boxShadow: isKingCell
                            ? 'inset 0 0 0 2px rgba(168,85,247,0.6)'
                            : undefined,
                          touchAction: 'pan-x pan-y',
                        }}
                      >
                        {cell.formatted}
                        {isKingCell ? (
                          <span
                            className="ml-0.5 inline-block text-[7px]"
                            style={{ color: textColor }}
                          >
                            ★
                          </span>
                        ) : null}
                      </td>
                    );
                  })}
                  {showTimeSpent && (
                    <td
                      style={{
                        width: TIME_SPENT_COLUMN_WIDTH,
                        minWidth: TIME_SPENT_COLUMN_WIDTH,
                        height: ROW_HEIGHT,
                        padding: `1px ${CELL_HORIZONTAL_PADDING}px`,
                        backgroundColor: 'var(--background)',
                        touchAction: 'pan-x pan-y',
                      }}
                    >
                      <TimeSpentCell
                        bucket={timeSpentMap.get(row.strike)}
                        maxPercent={maxTimeSpentPercent}
                      />
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
});

function TimeSpentCell({
  bucket,
  maxPercent,
}: {
  bucket: PriceDistributionBucket | undefined;
  maxPercent: number;
}) {
  const percent = bucket?.percent ?? 0;
  const width = percent > 0 ? Math.max(3, (percent / maxPercent) * 100) : 0;

  return (
    <div className="flex h-full items-center gap-1.5">
      <div className="h-3 flex-1 overflow-hidden rounded-[2px] bg-white/[.04]">
        <div
          className={`h-full rounded-[2px] ${
            bucket?.current === true ? 'bg-yellow-300' : 'bg-emerald-500/75'
          }`}
          style={{ width: `${width}%` }}
        />
      </div>
      <span className="w-8 text-right font-mono text-[9px] text-neutral-300">
        {percent.toFixed(1)}%
      </span>
    </div>
  );
}
